import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type {
  PlateCalculation,
  PlateInventoryEntry,
  PlateLoadMode,
} from '@heraion/shared';

import { Chip } from '../primitives/Chip';
import type { PlateLoadShape } from './state/plateConfig';
import type { WeightUnit } from './state/loggerDraft';
import type { useTheme } from './theme';
import type { makeStyles } from './plateCalcModalStyles';
import { PlateCalcComposeSection } from './PlateCalcComposeSection';
import { PlateCalcInputsSection } from './PlateCalcInputsSection';
import { PlateCalcSummary } from './PlateCalcSummary';

// The barbell half of the plate calculator: the loading shape (or the
// bodyweight sign), the compose row, the summary, and the bar and plate
// inventory. A fixed dumbbell has none of it (#49: it has no bar and no
// plates), so the modal mounts this only for a lift that is not handheld.

const LOAD_SHAPES: ReadonlyArray<PlateLoadShape> = ['standard', 'single-side'];

// Label + the sub-line that names the MECHANISM. The sub-line is not
// decoration: "coppie" vs "pila unica" IS what separates the two options, and
// it is what the approved mockup draws under each chip.
const SHAPE_KEYS: Record<PlateLoadShape, { label: string; sub: string }> = {
  standard: {
    label: 'mobile.plates.loadShapeTwoSides',
    sub: 'mobile.plates.loadShapeTwoSidesHint',
  },
  'single-side': {
    label: 'mobile.plates.loadShapeOneSide',
    sub: 'mobile.plates.loadShapeOneSideHint',
  },
};

type Props = {
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  isBodyweight: boolean;
  negative: boolean;
  setNegative: (negative: boolean) => void;
  loadShape: PlateLoadShape;
  onSelectShape: (shape: PlateLoadShape) => void;
  inventory: ReadonlyArray<PlateInventoryEntry>;
  calc: PlateCalculation;
  mode: PlateLoadMode;
  unit: WeightUnit;
  bar: number;
  barOptions: ReadonlyArray<number>;
  onSelectBar: (value: number) => void;
  onComposeDelta: (size: number, delta: number) => void;
  setPairs: (size: number, pairs: number) => void;
  openKey: number;
};

export function PlateCalcBarbellBody({
  theme,
  styles,
  isBodyweight,
  negative,
  setNegative,
  loadShape,
  onSelectShape,
  inventory,
  calc,
  mode,
  unit,
  bar,
  barOptions,
  onSelectBar,
  onComposeDelta,
  setPairs,
  openKey,
}: Props) {
  const { t } = useTranslation();
  const shapeLabel = t('mobile.plates.loadShapeLabel');

  return (
    <>
      {isBodyweight ? (
        <View style={styles.signRow}>
          <Text style={styles.sectionLabel}>
            {t('mobile.plates.signLabel', { defaultValue: 'EXTRA SIGN' })}
          </Text>
          <View
            accessibilityRole="radiogroup"
            accessibilityLabel={t('mobile.plates.signLabel', {
              defaultValue: 'EXTRA SIGN',
            })}
            style={styles.chipRow}
          >
            <Chip
              theme={theme}
              role="radio"
              shape="box"
              style={styles.chipFill}
              label={t('mobile.plates.signAdd', {
                defaultValue: '+ Weighted',
              })}
              selected={!negative}
              onPress={() => setNegative(false)}
            />
            <Chip
              theme={theme}
              role="radio"
              shape="box"
              style={styles.chipFill}
              label={t('mobile.plates.signAssist', {
                defaultValue: '− Assist',
              })}
              selected={negative}
              onPress={() => setNegative(true)}
            />
          </View>
        </View>
      ) : (
        // The shape sits HERE, above the summary, and not in the
        // configuration block below it: it changes the RESULT: what you
        // are calculating: where the bar and the plates describe the
        // equipment you own. My call, reversible (timeline-logger.md).
        <View style={styles.signRow}>
          <Text style={styles.sectionLabel}>{shapeLabel}</Text>
          <View
            accessibilityRole="radiogroup"
            accessibilityLabel={shapeLabel}
            style={styles.chipRow}
          >
            {LOAD_SHAPES.map((shape) => (
              <Chip
                key={shape}
                theme={theme}
                role="radio"
                shape="box"
                style={styles.chipShape}
                label={t(SHAPE_KEYS[shape].label)}
                sublabel={t(SHAPE_KEYS[shape].sub)}
                selected={loadShape === shape}
                onPress={() => onSelectShape(shape)}
              />
            ))}
          </View>
        </View>
      )}

      {isBodyweight ? null : (
        // ⛔ Weighted / assisted rows are out: their load hangs off a
        // belt, so "pairs" and "one side" don't describe it, and those
        // rows cannot even reach this modal until fetta D opens them.
        // Composing arrives with them, not before.
        <PlateCalcComposeSection
          theme={theme}
          styles={styles}
          inventory={inventory}
          loaded={calc.ok ? calc.pairsLoaded : []}
          mode={mode}
          unit={unit}
          onAdd={(size) => onComposeDelta(size, 1)}
        />
      )}

      <PlateCalcSummary
        styles={styles}
        calc={calc}
        unit={unit}
        showSingleSideHint={loadShape === 'single-side'}
        // Taking a plate off lives on the row that names it (Antenore
        // 10/09). Withheld on bodyweight rows for the same reason the
        // compose row is: a belt does not load in pairs.
        onRemovePlate={
          isBodyweight ? undefined : (size) => onComposeDelta(size, -1)
        }
      />
      <PlateCalcInputsSection
        key={`inputs-${openKey}`}
        theme={theme}
        styles={styles}
        bar={bar}
        barOptions={barOptions}
        onSelectBar={onSelectBar}
        inventory={inventory}
        unit={unit}
        onSetPairs={setPairs}
      />
    </>
  );
}
